import { Grid } from '@mui/material';
import { Language, validLanguages } from '@nx-smartmonkey/shared/domain';
import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import useSpeechToText, { ResultType } from 'react-hook-speech-to-text';
// import 'non.geist';

import { storageService } from '../../application/StorageService';
import MicrophoneIcon from '../../assets/svg/MicrophoneIcon';
import { StorageKeys } from '../../constants/StorageKeys';
import { useRetrieveActiveLanguage } from '../../hooks/translations/useRetrieveActiveLanguage';
import { Text } from '../../react-components/Text';
import configuration from '../../services/configuration';
import { Modal } from '../Modal';

interface RecordingModalProps {
  showModal: boolean;
  continuosVoiceInput?: boolean;
  onStartRecording: () => void;
  onStopRecording: (results: ResultType[] | string[]) => void;
  changeText?: (results: ResultType[] | string[]) => void;
}

const languageTransform: { [key: string]: string } = {
  en: `en-US`,
  es: `es-ES`,
  ca: `ca-ES`,
  pt: `pt-PT`,
  de: `de-DE`,
  hu: `hu-HU`,
  ro: `ro-RO`,
  fr: `fr-FR`,
  ru: `ru-RU`,
  fi: `fi-FI`,
  it: `it-IT`,
};

const getKeyLanguageTransform = (key: string) => {
  return languageTransform[key] ?? `en-US`;
};

export const RecordingModal = ({
  showModal,
  continuosVoiceInput,
  onStartRecording,
  onStopRecording,
  changeText,
}: RecordingModalProps) => {
  const [isSelectingLanguage, setIsSelectingLanguage] = useState<boolean>(false);
  const [isRecordingLocal, setIsRecordingLocal] = useState<boolean>(false);

  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    getKeyLanguageTransform(Language.getDefaultLanguage().value)
  );
  const { activeLanguage } = useRetrieveActiveLanguage();
  const [selectedValue, setSelectedValue] = useState<string>(Language.getDefaultLanguage().value);

  // Set the language from the local storage
  useEffect(() => {
    //Fetch the language
    const fetchLanguage = async () => {
      const localStorageLanguage = await storageService.getItem({ key: StorageKeys.SEARCH_VOICE_LANGUAGE });
      const activeLanguageLocale = activeLanguage ?? Language.getDefaultLanguage().value;
      setSelectedLanguage(getKeyLanguageTransform(localStorageLanguage ?? activeLanguageLocale));
      setSelectedValue(localStorageLanguage ?? activeLanguageLocale);
    };

    fetchLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start Recording when the modal is open
  useEffect(() => {
    if (showModal) {
      startRecording(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const { error, isRecording, results, startSpeechToText, stopSpeechToText, setResults } = useSpeechToText({
    googleApiKey: configuration.SPEECH_TO_TEXT_API_KEY,
    crossBrowser: true,
    useOnlyGoogleCloud: true,
    continuous: continuosVoiceInput ? true : false,
    useLegacyResults: false,
    timeout: 14000,
    googleCloudRecognitionConfig: {
      //más información de la configuración https://cloud.google.com/speech-to-text/docs/reference/rest/v1/RecognitionConfig
      encoding: `LINEAR16`,
      languageCode: selectedLanguage,
      model: `command_and_search`, //model: `default` para un modelo mas genérico, pero no funciona con alternativeLanguageCodes
      useEnhanced: true,
      // alternativeLanguageCodes: [`en-US`, `ca-ES`]. No disponible para la actual libreria
    },
  });

  // Segun la documentacion de react-hook-speech-to-text, el error solo salta si el browser no soporta la API que no es de google (no la usamos)
  if (error) {
    console.error(`error`, error);
  }

  // Stop recording if the user stops talking (timeout)
  useEffect(() => {
    if (!continuosVoiceInput && !isRecording && isRecordingLocal === true) {
      stopRecording(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  // Change text in  live and if countinuos mode, mantain the speech
  useEffect(() => {
    if (isRecordingLocal === false && isRecording && continuosVoiceInput === true) {
      stopSpeechToText();
    }
    if (changeText) {
      changeText(results);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  // Start and stop recording
  const startRecording = (callOnstartRecording = true) => {
    if (!isRecordingLocal) {
      startSpeechToText();
      setIsRecordingLocal(true);
      setResults([]);
      if (callOnstartRecording) {
        onStartRecording();
      }
    }
  };
  const stopRecording = (closeMenu = false) => {
    if (isRecordingLocal) {
      stopSpeechToText();
      setIsRecordingLocal(false);
      if (closeMenu) {
        onStopRecording(results);
      }
    }
  };

  // Handle change language
  const handleChangeSelect = (language: string) => {
    setSelectedValue(language);
    setSelectedLanguage(getKeyLanguageTransform(language));
    storageService.setItem({ key: StorageKeys.SEARCH_VOICE_LANGUAGE, item: language });
  };

  return (
    <Modal
      open={showModal}
      onBackgroundClick={() => {
        stopRecording(true);
      }}
    >
      <Grid
        onClick={(e) => e.stopPropagation()}
        container
        direction="column"
        style={{
          backgroundColor: RoutalPalette.white,
          display: `flex`,
          flexWrap: `wrap`,
          alignItems: `center`,
          flexDirection: `column`,
        }}
      >
        <Grid item>
          <Text variant="h6" intlId="driver.stop.search.voice.speak_address" />
        </Grid>

        <Grid
          item
          container
          style={{
            width: `100%`,
            height: `180px`,
            display: `flex`,
            flexWrap: `wrap`,
            justifyContent: `center`,
            alignItems: `center`,
            flexDirection: `row`,
            position: `relative`,
          }}
        >
          <motion.div
            animate={{
              opacity: isSelectingLanguage ? 0.2 : [0.5, 0.2, 0.5],
              scale: isSelectingLanguage ? 1 : [1, 0.9, 1],
            }}
            transition={{ duration: isSelectingLanguage ? 0 : 2, repeat: Infinity }}
            style={{
              height: `60%`,
              aspectRatio: `1/1`,
              backgroundColor: RoutalPalette.secondary40,
              borderRadius: `50%`,
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              position: `absolute`,
            }}
          />
          <div
            style={{
              height: `50%`,
              aspectRatio: `1/1`,
              backgroundColor: RoutalPalette.secondary40,
              borderRadius: `50%`,
              position: `absolute`,
              display: `flex`,
              flexWrap: `wrap`,
              justifyContent: `center`,
              alignItems: `center`,
              flexDirection: `column`,
            }}
          >
            <MicrophoneIcon style={{ fill: RoutalPalette.oldWhite, height: `37px` }} />
          </div>
        </Grid>
        <Grid item style={{ width: `80%` }}>
          <RoutalSelect
            id="select-voice-language"
            style={{
              backgroundColor: `transparent`,
              height: `49px`,
            }}
            hideSeparator
            popoverProps={{
              options: validLanguages.map((language) => {
                return {
                  key: `option-${language}`,
                  listItemStyles: {
                    whiteSpace: `normal` as any,
                    fontSize: `12px`,
                    backgroundColor: selectedValue === language ? RoutalPalette.primary40n : RoutalPalette.white,
                  },
                  onClick: () => {
                    handleChangeSelect(language);
                  },
                  content: (
                    <Grid container key={`language-option-${language}`}>
                      <Text intlId={`driver.stop.search.voice.${language}`} />
                    </Grid>
                  ),
                };
              }),
              onOpen: () => {
                setIsSelectingLanguage(true);
                stopRecording(false);
              },
              onBlur: () => {
                setIsSelectingLanguage(false);
                startRecording();
              },
            }}
          >
            <Grid key={`selected-language-${selectedValue}`} container>
              <Text intlId={`driver.stop.search.voice.${selectedValue}`} />
            </Grid>
          </RoutalSelect>
        </Grid>
      </Grid>
    </Modal>
  );
};
